import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  CreditCard,
  AccountBalance,
  Check,
  Delete,
  Print,
  Email,
  CheckCircle,
  AttachMoney,
} from '@mui/icons-material';
import api from '../services/api';

const PaymentDialog = ({ open, onClose, invoice, onPaymentSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('');
  const [saveCard, setSaveCard] = useState(false);

  // Credit card form fields
  const [cardNumber, setCardNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [billingZip, setBillingZip] = useState('');

  // Check payment fields
  const [checkNumber, setCheckNumber] = useState('');

  // Bank transfer fields
  const [referenceNumber, setReferenceNumber] = useState('');

  useEffect(() => {
    if (invoice && open) {
      const balanceDue = invoice.total_amount - invoice.amount_paid;
      setPaymentAmount(balanceDue.toFixed(2));
      fetchSavedCards();
      resetForm();
    }
  }, [invoice, open]);

  const fetchSavedCards = async () => {
    if (!invoice?.client_id) return;

    try {
      const response = await api.get(`/payments/saved-cards/${invoice.client_id}`);
      setSavedCards(response.data || []);
    } catch (error) {
      console.error('Error fetching saved cards:', error);
    }
  };

  const resetForm = () => {
    setPaymentMethod('credit_card');
    setError('');
    setSuccess(false);
    setReceipt(null);
    setSelectedCard('');
    setSaveCard(false);
    setCardNumber('');
    setExpirationDate('');
    setCvv('');
    setCardholderName('');
    setBillingZip('');
    setCheckNumber('');
    setReferenceNumber('');
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.substring(0, 16);
    const formatted = limited.replace(/(\d{4})/g, '$1 ').trim();
    return formatted;
  };

  const formatExpirationDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.substring(0, 4);
    if (limited.length >= 2) {
      return `${limited.substring(0, 2)}/${limited.substring(2)}`;
    }
    return limited;
  };

  const validateCreditCardForm = () => {
    if (selectedCard) return true;

    if (!cardNumber || cardNumber.replace(/\s/g, '').length < 13) {
      setError('Invalid card number');
      return false;
    }
    if (!expirationDate || expirationDate.length !== 5) {
      setError('Invalid expiration date (MM/YY)');
      return false;
    }
    if (!cvv || cvv.length < 3) {
      setError('Invalid CVV');
      return false;
    }
    if (!cardholderName.trim()) {
      setError('Cardholder name is required');
      return false;
    }
    if (!billingZip || billingZip.length < 5) {
      setError('Invalid billing zip code');
      return false;
    }
    return true;
  };

  const validatePaymentAmount = () => {
    const amount = parseFloat(paymentAmount);
    const balanceDue = invoice.total_amount - invoice.amount_paid;

    if (isNaN(amount) || amount <= 0) {
      setError('Payment amount must be greater than 0');
      return false;
    }
    if (amount > balanceDue) {
      setError('Payment amount cannot exceed balance due');
      return false;
    }
    return true;
  };

  const handlePayment = async () => {
    setError('');

    if (!validatePaymentAmount()) return;

    if (paymentMethod === 'credit_card' && !validateCreditCardForm()) return;

    if (paymentMethod === 'check' && !checkNumber.trim()) {
      setError('Check number is required');
      return;
    }

    if (paymentMethod === 'bank_transfer' && !referenceNumber.trim()) {
      setError('Reference number is required');
      return;
    }

    setProcessing(true);

    try {
      const paymentData = {
        invoice_id: invoice.id,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
      };

      if (paymentMethod === 'credit_card') {
        if (selectedCard) {
          paymentData.saved_card_id = selectedCard;
        } else {
          paymentData.card_details = {
            card_number: cardNumber.replace(/\s/g, ''),
            expiration_date: expirationDate,
            cvv: cvv,
            cardholder_name: cardholderName,
            billing_zip: billingZip,
          };
          paymentData.save_card = saveCard;
        }
      } else if (paymentMethod === 'check') {
        paymentData.check_number = checkNumber;
      } else if (paymentMethod === 'bank_transfer') {
        paymentData.reference_number = referenceNumber;
      }

      const response = await api.post('/payments', paymentData);

      setReceipt(response.data);
      setSuccess(true);

      if (onPaymentSuccess) {
        onPaymentSuccess(response.data);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.detail || 'Payment processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteSavedCard = async (cardId) => {
    try {
      await api.delete(`/payments/saved-cards/${cardId}`);
      setSavedCards(savedCards.filter(card => card.id !== cardId));
    } catch (error) {
      console.error('Error deleting card:', error);
      setError('Failed to delete saved card');
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleEmailReceipt = async () => {
    try {
      await api.post(`/payments/${receipt.id}/send-receipt`);
      setError('');
      alert('Receipt sent successfully');
    } catch (error) {
      setError('Failed to send receipt');
    }
  };

  const handleClose = () => {
    if (!processing) {
      resetForm();
      onClose();
    }
  };

  if (!invoice) return null;

  const balanceDue = invoice.total_amount - invoice.amount_paid;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {success ? 'Payment Receipt' : 'Process Payment'}
          </Typography>
          <IconButton onClick={handleClose} disabled={processing}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {success && receipt ? (
          <Box>
            <Box textAlign="center" mb={3}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Payment Successful
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Transaction ID: {receipt.transaction_id}
              </Typography>
            </Box>

            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Number
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {invoice.invoice_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Client
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {invoice.client_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {receipt.payment_method.replace('_', ' ').toUpperCase()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {new Date(receipt.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6">Amount Paid</Typography>
                      <Typography variant="h6" color="success.main" fontWeight="bold">
                        ${receipt.amount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  {receipt.remaining_balance !== undefined && (
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Remaining Balance
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${receipt.remaining_balance.toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box>
            {/* Invoice Summary */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Number
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {invoice.invoice_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Client
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {invoice.client_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="body1">
                      ${invoice.total_amount.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Amount Paid
                    </Typography>
                    <Typography variant="body1">
                      ${invoice.amount_paid.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">Balance Due</Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ${balanceDue.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Payment Amount */}
            <TextField
              fullWidth
              label="Payment Amount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                max: balanceDue,
                step: 0.01,
              }}
              sx={{ mb: 3 }}
              disabled={processing}
            />

            {/* Payment Method Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentMethod}
                label="Payment Method"
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={processing}
              >
                <MenuItem value="credit_card">
                  <Box display="flex" alignItems="center">
                    <CreditCard sx={{ mr: 1 }} />
                    Credit Card
                  </Box>
                </MenuItem>
                <MenuItem value="cash">
                  <Box display="flex" alignItems="center">
                    <AttachMoney sx={{ mr: 1 }} />
                    Cash
                  </Box>
                </MenuItem>
                <MenuItem value="check">
                  <Box display="flex" alignItems="center">
                    <Check sx={{ mr: 1 }} />
                    Check
                  </Box>
                </MenuItem>
                <MenuItem value="bank_transfer">
                  <Box display="flex" alignItems="center">
                    <AccountBalance sx={{ mr: 1 }} />
                    Bank Transfer
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Credit Card Section */}
            {paymentMethod === 'credit_card' && (
              <Box>
                {savedCards.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Saved Payment Methods
                    </Typography>
                    <List>
                      {savedCards.map((card) => (
                        <Card key={card.id} variant="outlined" sx={{ mb: 1 }}>
                          <ListItem>
                            <Radio
                              checked={selectedCard === card.id}
                              onChange={() => setSelectedCard(card.id)}
                              disabled={processing}
                            />
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center">
                                  <CreditCard sx={{ mr: 1 }} />
                                  <Typography>
                                    {card.card_brand} •••• {card.last_four}
                                  </Typography>
                                </Box>
                              }
                              secondary={`Expires ${card.exp_month}/${card.exp_year}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleDeleteSavedCard(card.id)}
                                disabled={processing}
                              >
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </Card>
                      ))}
                    </List>
                    <Divider sx={{ my: 2 }}>
                      <Chip label="OR USE NEW CARD" size="small" />
                    </Divider>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Card Number"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      placeholder="1234 5678 9012 3456"
                      disabled={processing || !!selectedCard}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CreditCard />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Expiration Date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(formatExpirationDate(e.target.value))}
                      placeholder="MM/YY"
                      disabled={processing || !!selectedCard}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="CVV"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 4))}
                      placeholder="123"
                      type="password"
                      disabled={processing || !!selectedCard}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cardholder Name"
                      value={cardholderName}
                      onChange={(e) => setCardholderName(e.target.value)}
                      placeholder="John Doe"
                      disabled={processing || !!selectedCard}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Billing Zip Code"
                      value={billingZip}
                      onChange={(e) => setBillingZip(e.target.value.replace(/\D/g, '').substring(0, 5))}
                      placeholder="12345"
                      disabled={processing || !!selectedCard}
                    />
                  </Grid>
                  {!selectedCard && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={saveCard}
                            onChange={(e) => setSaveCard(e.target.checked)}
                            disabled={processing}
                          />
                        }
                        label="Save card for future use"
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Check Payment */}
            {paymentMethod === 'check' && (
              <TextField
                fullWidth
                label="Check Number"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                placeholder="Enter check number"
                disabled={processing}
              />
            )}

            {/* Bank Transfer */}
            {paymentMethod === 'bank_transfer' && (
              <TextField
                fullWidth
                label="Reference Number"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Enter bank transfer reference number"
                disabled={processing}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {success ? (
          <>
            <Button onClick={handleClose}>Close</Button>
            <Button startIcon={<Print />} onClick={handlePrintReceipt}>
              Print Receipt
            </Button>
            <Button
              variant="contained"
              startIcon={<Email />}
              onClick={handleEmailReceipt}
            >
              Email Receipt
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={processing}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={processing}
              startIcon={processing ? <CircularProgress size={16} /> : null}
            >
              {processing ? 'Processing...' : 'Process Payment'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

PaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoice: PropTypes.object,
  onPaymentSuccess: PropTypes.func,
};

export default PaymentDialog;
